// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { DetailPageBody } from '@/components/detail-page-body';
import { DetailPageHeader } from '@/components/detail-page-header';
import { DetailPageHeaderField } from '@/components/detail-page-header-field';
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import { HorizontalNavigationLink } from '@/components/horizontal-navigation-link';
import { Link } from '@/components/link';
import { NotFoundErrorBoundary } from '@/components/not-found-error-boundary';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import { TransportRefLabel } from '@/components/transport-ref-label';
import {
  ContentCopyRounded,
  EditOutlined,
  InfoOutlined,
  Inventory,
  LocalShipping,
} from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import { LocationIntegrationStatus } from '@/location/components/location-integration-status';
import {
  getEffectiveAddress,
  getEffectiveLocationName,
  isEffectiveThirdPartyEnabled,
} from '@/location/utilities/get-effective-third-party';
import { RETURN_ORDER_REASON_LABELS } from '@/return-order/api/types';
import { ReturnOrderAttachmentsSection } from '@/return-order/components/return-order-attachments-section';
import { ReturnOrderAuditCard } from '@/return-order/components/return-order-audit-card';
import { ReturnOrderCancelDialog } from '@/return-order/components/return-order-cancel-dialog';
import { ReturnOrderDetailsSkeleton } from '@/return-order/components/return-order-details-skeleton';
import { ReturnOrderDialog } from '@/return-order/components/return-order-dialog';
import { ReturnOrderNotesControl } from '@/return-order/components/return-order-notes-control';
import { ReturnOrderPhaseIndicator } from '@/return-order/components/return-order-phase-indicator';
import { ReturnOrderSendTo3PLDialog } from '@/return-order/components/return-order-send-to-3pl-dialog';
import { ReturnOrderSyncSignal } from '@/return-order/components/return-order-sync-signal';
import { isCancelled, isFullyReceived } from '@/return-order/constants/status';
import { useReturnOrder } from '@/return-order/hooks/use-return-order';
import {
  returnOrderDetailQueryKey,
  returnOrdersQueryKey,
} from '@/return-order/query-keys';
import { ReturnOrderFormData } from '@/return-order/schemas/return-order-form-schema';
import { InboundShipmentDialog } from '@/shipment/components/inbound-shipment-dialog';
import { extractSparseItem } from '@/sparse/utilities/to-sparse-item';
import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { Outlet, createFileRoute } from '@tanstack/react-router';
import { Fragment, Suspense, useMemo } from 'react';

export const Route = createFileRoute('/_app/return-orders/$returnOrderKey')({
  component: ReturnOrderRoute,
});

function ReturnOrderRoute() {
  const { returnOrderKey } = Route.useParams();

  return (
    <NotFoundErrorBoundary
      entityType="return order"
      listPath="/return-orders"
      listLabel="Go to return orders"
    >
      <Suspense fallback={<ReturnOrderDetailsSkeleton />}>
        <ReturnOrderContent returnOrderKey={returnOrderKey} />
      </Suspense>
    </NotFoundErrorBoundary>
  );
}

function ReturnOrderContent(props: { returnOrderKey: string }) {
  const { returnOrder } = useReturnOrder(props.returnOrderKey);
  const queryClient = useQueryClient();
  const invalidateAfterDriftPull = () => {
    void queryClient.invalidateQueries({ queryKey: returnOrdersQueryKey });
    void queryClient.invalidateQueries({ queryKey: ['location'] });
  };
  const editDialog = useDisclosure();
  const shipmentDialog = useDisclosure();
  const cancelDialog = useDisclosure();
  const sendTo3PLDialog = useDisclosure();
  const cloneDialog = useDisclosure();

  // Build clone form data: pre-populate reason, locations, and items
  const cloneFormData = useMemo((): ReturnOrderFormData => {
    return {
      returnType: returnOrder.reason,
      returnReason: returnOrder.returnReason ?? null,
      fromLocation: returnOrder.fromLocation,
      toLocation: returnOrder.toLocation,
      promiseShipDate: null,
      promiseDeliveryDate: null,
      orderNotes: null,
      carrier: null,
      referenceNumber: null,
      otherCarrier: null,
      items: returnOrder.items.map((item) => ({
        item: extractSparseItem(item),
        quantity: item.requestedQuantity,
      })),
    };
  }, [returnOrder]);

  const handleCancelSuccess = (updatedReturnOrder: typeof returnOrder) => {
    queryClient.setQueryData(
      returnOrderDetailQueryKey(props.returnOrderKey),
      updatedReturnOrder
    );
    queryClient.invalidateQueries({
      queryKey: returnOrdersQueryKey,
      refetchType: 'active',
    });
  };

  const handleSendTo3PLSuccess = (updatedReturnOrder: typeof returnOrder) => {
    queryClient.setQueryData(
      returnOrderDetailQueryKey(props.returnOrderKey),
      updatedReturnOrder
    );
    queryClient.invalidateQueries({
      queryKey: returnOrdersQueryKey,
      refetchType: 'active',
    });
  };

  // Determine if destination is 3PL-enabled (zones inherit from parent)
  const isThirdPartyDestination = isEffectiveThirdPartyEnabled(
    returnOrder.toLocation
  );

  // An order sent to a 3PL is editable only once its destination is disconnected.
  const isEditable =
    returnOrder.status === 'DRAFT' ||
    (returnOrder.status === 'AWAITING_3PL' && !isThirdPartyDestination);

  // Show "Send to 3PL" only for draft orders going to a 3PL location
  const showSendTo3PL =
    returnOrder.status === 'DRAFT' && isThirdPartyDestination;

  // Show "Fulfill" for non-3PL locations (any status except cancelled/fully received)
  // RECEIVED_PARTIAL still allows additional shipments
  const showFulfill =
    !isCancelled(returnOrder.status) &&
    !isFullyReceived(returnOrder.status) &&
    !isThirdPartyDestination;

  // Can only cancel in draft status
  const canCancel = returnOrder.status === 'DRAFT';

  // Block draft-advancing actions when any line item has zero quantity.
  // Legacy orders may carry zero-qty rows; the user must edit them first.
  // Only applies in DRAFT — once the order has moved past DRAFT the user
  // can no longer Edit, so blocking would leave them with no path forward.
  const blockDraftAdvance =
    returnOrder.status === 'DRAFT' &&
    returnOrder.items.some((item) => item.requestedQuantity === 0);
  const zeroQuantityTooltip =
    'Edit the order and remove or set a positive quantity on every line item before continuing';

  return (
    <Container maxWidth="xl">
      {/* Edit dialog */}
      <ReturnOrderDialog
        isOpen={editDialog.isOpen}
        onClose={editDialog.close}
        returnOrder={returnOrder}
      />

      {/* Shipment dialog */}
      <InboundShipmentDialog
        isOpen={shipmentDialog.isOpen}
        onClose={shipmentDialog.close}
        mode="return-order"
        returnOrderKey={props.returnOrderKey}
        dialogTitle="Record a return shipment"
      />

      {/* Cancel dialog */}
      <ReturnOrderCancelDialog
        isOpen={cancelDialog.isOpen}
        returnOrderKey={props.returnOrderKey}
        returnOrderNumber={returnOrder.returnOrderNumber}
        onClose={cancelDialog.close}
        onSuccess={handleCancelSuccess}
      />

      {/* Send to 3PL dialog */}
      <ReturnOrderSendTo3PLDialog
        isOpen={sendTo3PLDialog.isOpen}
        returnOrderKey={props.returnOrderKey}
        returnOrderNumber={returnOrder.returnOrderNumber}
        onClose={sendTo3PLDialog.close}
        onSuccess={handleSendTo3PLSuccess}
      />

      {/* Clone Return Order Dialog */}
      <ReturnOrderDialog
        isOpen={cloneDialog.isOpen}
        onClose={cloneDialog.close}
        initialFormData={cloneFormData}
      />

      <DetailPageHeader
        parentBreadcrumb={
          <Link
            to="/return-orders"
            variant="body2"
            color="text.secondary"
            sx={{ display: 'block' }}
          >
            Return orders
          </Link>
        }
        currentBreadcrumb={returnOrder.returnOrderNumber}
        title={returnOrder.returnOrderNumber}
        titleAdornment={
          returnOrder.exchangeGroup ? (
            <Chip label="Exchange" size="small" />
          ) : null
        }
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            {isEditable && (
              <Button
                variant="outlined"
                startIcon={<EditOutlined />}
                onClick={editDialog.open}
              >
                Edit
              </Button>
            )}
            {/* 3PL sync-status signal for any 3PL-bound order */}
            {returnOrder.thirdParty.isEnabled && (
              <ReturnOrderSyncSignal
                returnOrderKey={returnOrder.key}
                returnOrderNumber={returnOrder.returnOrderNumber}
                status={returnOrder.status}
                lastSyncedAt={returnOrder.thirdParty.lastSyncedAt}
                lastThirdPartyUpdateAt={returnOrder.thirdParty.lastUpdateAt}
                hasSyncError={returnOrder.thirdParty.hasSyncError}
                syncErrorMessage={returnOrder.thirdParty.syncErrorMessage ?? ''}
              />
            )}
            {showSendTo3PL && (
              <Tooltip
                arrow
                title={blockDraftAdvance ? zeroQuantityTooltip : ''}
              >
                <span>
                  <Button
                    variant="contained"
                    startIcon={<LocalShipping />}
                    onClick={sendTo3PLDialog.open}
                    disabled={blockDraftAdvance}
                  >
                    Send to 3PL
                  </Button>
                </span>
              </Tooltip>
            )}
            {showFulfill && (
              <Tooltip
                arrow
                title={blockDraftAdvance ? zeroQuantityTooltip : ''}
              >
                <span>
                  <Button
                    variant="contained"
                    startIcon={<Inventory />}
                    onClick={shipmentDialog.open}
                    disabled={blockDraftAdvance}
                  >
                    Fulfill
                  </Button>
                </span>
              </Tooltip>
            )}
            {canCancel && (
              <Button
                variant="contained"
                color="error"
                onClick={cancelDialog.open}
              >
                Cancel order
              </Button>
            )}
            {/* Clone button */}
            <Tooltip arrow title="Clone this return order">
              <IconButton size="small" onClick={cloneDialog.open}>
                <ContentCopyRounded fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        }
      >
        {returnOrder.exchangeGroup && (
          <Typography variant="body2" color="text.secondary">
            Part of exchange <b>{returnOrder.exchangeGroup.exchangeNumber}</b>
            {returnOrder.exchangeGroup.fulfillmentOrders.length > 0 && (
              <>
                {' (linked to '}
                {returnOrder.exchangeGroup.fulfillmentOrders.map(
                  (fo, index) => (
                    <Fragment key={fo.key}>
                      {index > 0 && ', '}
                      <Link
                        to="/fulfillments/$fulfillmentKey"
                        params={{ fulfillmentKey: fo.key }}
                      >
                        {fo.fulfillmentOrderNumber}
                      </Link>
                    </Fragment>
                  )
                )}
                {')'}
              </>
            )}
          </Typography>
        )}
        <DetailPageHeaderField label="Type">
          {RETURN_ORDER_REASON_LABELS[returnOrder.reason]}
        </DetailPageHeaderField>
        <DetailPageHeaderField label="Reason">
          {returnOrder.returnReason || 'Not set'}
        </DetailPageHeaderField>
      </DetailPageHeader>

      {/* Main content */}
      <DetailPageBody
        side={
          <>
            {/* Notes Section */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <HardfinTabCardWrapper title="Notes" hideDivider>
                <Box sx={{ height: '12.4rem' }}>
                  <ReturnOrderNotesControl
                    returnOrder={returnOrder}
                    isDestinationThirdParty={isThirdPartyDestination}
                  />
                </Box>
              </HardfinTabCardWrapper>
            </Box>

            {/* Attachments section (files + links) */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <ReturnOrderAttachmentsSection
                returnOrderKey={props.returnOrderKey}
                returnOrderId={returnOrder.id}
              />
            </Box>

            {/* Audit log */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <ReturnOrderAuditCard returnOrderKey={props.returnOrderKey} />
            </Box>
          </>
        }
      >
        {/* Phase indicator and locations card */}
        <Paper sx={{ minWidth: 0, padding: 3 }} variant="outlined">
          {/* Phase Indicator */}
          <ReturnOrderPhaseIndicator
            currentStatus={returnOrder.status}
            cancelledReason={returnOrder.cancellationReason}
            isThirdPartyEnabled={isThirdPartyDestination}
            createdAt={returnOrder.createdAt}
            shippedAt={returnOrder.mostRecentShippedAt}
            deliveredAt={returnOrder.mostRecentDeliveredAt}
            receivedAt={returnOrder.mostRecentReceivedAt}
          />

          {/* From Location */}
          <Box marginTop={4} paddingX={1}>
            <Stack direction="row" justifyContent="space-between">
              <Stack direction="column" spacing={0.5}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography variant="subtitle2">From: </Typography>
                  <Typography variant="body2">
                    {returnOrder.fromLocation
                      ? getEffectiveLocationName(returnOrder.fromLocation)
                      : 'Not set'}
                  </Typography>
                  <LocationIntegrationStatus
                    location={returnOrder.fromLocation}
                    onResolved={invalidateAfterDriftPull}
                  />
                  {isEffectiveThirdPartyEnabled(returnOrder.fromLocation) && (
                    <Chip label="3PL" size="small" />
                  )}
                </Stack>
                {returnOrder.fromLocation &&
                  getEffectiveAddress(returnOrder.fromLocation) && (
                    <Typography variant="body2" color="text.secondary">
                      {getEffectiveAddress(returnOrder.fromLocation)}
                    </Typography>
                  )}
              </Stack>
              <Stack direction="column" alignItems="flex-end" spacing={0.5}>
                <Typography variant="body2" color="text.secondary">
                  Promise ship
                </Typography>
                <Typography
                  variant="body2"
                  color={
                    returnOrder.promiseShipDate
                      ? 'text.primary'
                      : 'text.secondary'
                  }
                >
                  {returnOrder.promiseShipDate?.toLocaleString({
                    dateStyle: 'medium',
                  }) || 'Not set'}
                </Typography>
              </Stack>
            </Stack>
          </Box>

          {/* To Location */}
          <Box marginTop={2} paddingX={1}>
            <Stack direction="row" justifyContent="space-between">
              <Stack direction="column" spacing={0.5}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography variant="subtitle2">To: </Typography>
                  <Typography variant="body2">
                    {getEffectiveLocationName(returnOrder.toLocation)}
                  </Typography>
                  <LocationIntegrationStatus
                    location={returnOrder.toLocation}
                    onResolved={invalidateAfterDriftPull}
                  />
                  {isEffectiveThirdPartyEnabled(returnOrder.toLocation) && (
                    <Chip label="3PL" size="small" />
                  )}
                </Stack>
                {getEffectiveAddress(returnOrder.toLocation) && (
                  <Typography variant="body2" color="text.secondary">
                    {getEffectiveAddress(returnOrder.toLocation)}
                  </Typography>
                )}
              </Stack>
              <Stack direction="column" alignItems="flex-end" spacing={0.5}>
                <Typography variant="body2" color="text.secondary">
                  Promise delivery
                </Typography>
                <Typography
                  variant="body2"
                  color={
                    returnOrder.promiseDeliveryDate
                      ? 'text.primary'
                      : 'text.secondary'
                  }
                >
                  {returnOrder.promiseDeliveryDate?.toLocaleString({
                    dateStyle: 'medium',
                  }) || 'Not set'}
                </Typography>
              </Stack>
            </Stack>
          </Box>

          {/* Transport ref */}
          {(returnOrder.transportReferenceDetails?.carrier ||
            returnOrder.transportReferenceDetails?.transportReference) && (
            <Box marginTop={2} paddingX={1}>
              <Divider sx={{ borderStyle: 'dashed', mb: 2 }} />
              <Stack direction="row" justifyContent="flex-end">
                <Stack direction="column" alignItems="flex-end" spacing={0.5}>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="body2" color="text.secondary">
                      Transport ref
                    </Typography>
                    <Tooltip
                      arrow
                      title="Master tracking or carrier reference for this return order"
                    >
                      <InfoOutlined
                        sx={{ fontSize: 16, color: 'text.secondary' }}
                      />
                    </Tooltip>
                  </Stack>
                  <TransportRefLabel
                    carrier={
                      returnOrder.transportReferenceDetails?.carrier ?? null
                    }
                    carrierName={
                      returnOrder.transportReferenceDetails?.name ?? null
                    }
                    trackingNumber={
                      returnOrder.transportReferenceDetails
                        ?.transportReference ?? null
                    }
                  />
                </Stack>
              </Stack>
            </Box>
          )}
        </Paper>

        {/* Tabs section */}
        <Box sx={{ minWidth: 0 }}>
          <Stack
            direction="row"
            marginBottom={2}
            sx={{ borderBottom: '1px solid #E0E0E0' }}
            marginX={1}
          >
            <HorizontalNavigationLink
              to="/return-orders/$returnOrderKey"
              params={{ returnOrderKey: props.returnOrderKey }}
              activeOptions={{ exact: true }}
            >
              Line items
            </HorizontalNavigationLink>
            <HorizontalNavigationLink
              to="/return-orders/$returnOrderKey/shipments"
              params={{ returnOrderKey: props.returnOrderKey }}
            >
              Shipments
            </HorizontalNavigationLink>
          </Stack>

          <SupportErrorBoundary message="An error occurred.">
            <Outlet />
          </SupportErrorBoundary>
        </Box>
      </DetailPageBody>
    </Container>
  );
}
